import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Box,
  Typography,
  TableFooter,
  TablePagination,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * rows: income report records from API
 * summary: { totalSales, totalPaid, remaining }
 * themeColors: tokens(theme.palette.mode)
 */
const IncomeTable = ({ rows = [], summary = {}, themeColors }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const displayRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: "65vh",
        border: 2,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Table stickyHeader>
        <TableHead sx={{ backgroundColor: themeColors.blueAccent[700] }}>
          <TableRow>
            <TableCell />
            <TableCell>Sales Date</TableCell>
            <TableCell>Salesperson</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>Total Sales</TableCell>
            <TableCell>Paid</TableCell>
            <TableCell>Remaining</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {displayRows.map((row, index) => (
            <IncomeRow
              key={index}
              row={row}
              themeColors={themeColors}
            />
          ))}
        </TableBody>

        {/* Totals */}
        <TableFooter>
          <TableRow sx={{ backgroundColor: themeColors.primary[400] }}>
            <TableCell colSpan={4} align="right" sx={{ fontWeight: "bold" }}>
              Totals:
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {Number(summary.totalSales || 0).toLocaleString()}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {Number(summary.totalPaid || 0).toLocaleString()}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {Number(summary.remaining || 0).toLocaleString()}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </TableContainer>
  );
};

const IncomeRow = ({ row, themeColors }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Parent row */}
      <TableRow hover sx={{ cursor: "pointer" }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>

        <TableCell>
          {dayjs.utc(row.salesDate).format("YYYY-MM-DD")}
        </TableCell>

        <TableCell>{row.User?.fullName || "N/A"}</TableCell>

        <TableCell>
          {row.Customer?.name || "Direct Sale"}
        </TableCell>

        <TableCell>
          {Number(row.items?.reduce(
            (sum, i) => sum + Number(i.totalPrice || 0),
            0
          )).toLocaleString()}
        </TableCell>

        <TableCell>
          {Number(row.paidAmount || 0).toLocaleString()}
        </TableCell>

        <TableCell>
          {Number(row.remainingAmount || 0).toLocaleString()}
        </TableCell>
      </TableRow>

      {/* Collapsible items */}
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Items
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Warehouse</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Sale Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {row.items?.map((item, idx) => (
                    <TableRow
                      key={idx}
                      sx={{ "&:last-child td": { border: 0 } }}
                    >
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>{item.Warehouse?.name || "N/A"}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {Number(item.salePrice).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell align="right">
                        {Number(item.totalPrice).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default IncomeTable;
